import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";
import { useCompaniesSearchCountQuery } from "app/pages/sales-intelligence/modules/leads-search/query/queries/useCompaniesSearchCountQuery";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";

import { StyledCompaniesCountHighlight } from "./styles";

type Props = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    isEnabled: boolean;
    dataAutomationPrefix: string;
    companiesCountTranslationKey?: string;
};

type CompaniesCountLabelProps = {
    formattedCount: string;
    translationKey: string;
};

const CompaniesCountLabel = ({ formattedCount, translationKey }: CompaniesCountLabelProps) => {
    const { translate } = useTranslate();
    const fullText = translate(translationKey, {
        count: formattedCount,
    });

    if (!fullText.includes(formattedCount)) {
        return fullText;
    }

    const [before, after] = fullText.split(formattedCount);

    return (
        <>
            {before}
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon["500"]}
            >
                {formattedCount}
            </Text>
            {after}
        </>
    );
};

export const SelectedCriteriaCompaniesCount = ({
    panelValue,
    isEnabled,
    dataAutomationPrefix,
    companiesCountTranslationKey = leadsSearchFiltersI18nKeys.selectedFiltersCompaniesCount,
}: Props) => {
    const formatNumber = abbrNumberFilter();
    const companiesCountAutomation = `${dataAutomationPrefix}.selected-sidebar.companies-count`;

    const { totalCount, isLoading, isError } = useCompaniesSearchCountQuery({
        panelValue,
        isEnabled,
    });

    if (!isEnabled || isError) {
        return null;
    }

    if (isLoading) {
        return (
            <StyledCompaniesCountHighlight
                inline
                data-automation={companiesCountAutomation}
            >
                <PixelPlaceholderLoader
                    width={220}
                    height={16}
                />
            </StyledCompaniesCountHighlight>
        );
    }

    if (totalCount === undefined) {
        return null;
    }

    const formattedCount = formatNumber(totalCount);

    return (
        <StyledCompaniesCountHighlight
            inline
            data-automation={companiesCountAutomation}
        >
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon["500"]}
            >
                <CompaniesCountLabel
                    formattedCount={formattedCount}
                    translationKey={companiesCountTranslationKey}
                />
            </Text>
        </StyledCompaniesCountHighlight>
    );
};
